/**
 * The file sections of `cycle-step.html` for step 4 (f10.7, design D10): the
 * drop zone, "File dropped" and "What the system did with it", plus the
 * "Resulting rates" preview. Every fact and figure is the batch's own.
 */

import { useRef, useState, type ReactNode } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, FileSpreadsheet, Repeat, UploadCloud } from 'lucide-react';

import type { RateCell, SalesRateFigures, SalesRateReview } from '@/api/client';
import { formatBytes, formatWhen, shortChecksum, statePresentation } from '@/routes/data-import/importView';
import { EmptyCell } from '@/ui/EmptyCell';

import { MONTH_LABELS, cellKind, compactDecimal, decisionPill, severityPill, transformations } from './salesRatesView';

function useFilePicker(onFile: (file: File) => void, label: string) {
  const input = useRef<HTMLInputElement>(null);
  const element = (
    <input
      ref={input}
      type="file"
      className="sr-only"
      aria-label={label}
      onChange={(event) => {
        const file = event.target.files?.item(0);
        if (file) onFile(file);
        event.target.value = '';
      }}
    />
  );
  return { open: () => input.current?.click(), element };
}

export function SalesRatesDropzone({
  scopeName,
  currency,
  onFile,
  busy,
}: {
  scopeName: string;
  currency: string;
  onFile: (file: File) => void;
  busy: boolean;
}) {
  const [over, setOver] = useState(false);
  const picker = useFilePicker(onFile, `COSMA sales-rate file for ${scopeName}`);
  return (
    <div
      className={`dropzone${over ? ' is-over' : ''}`}
      onDragOver={(event) => {
        event.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(event) => {
        event.preventDefault();
        setOver(false);
        const file = event.dataTransfer.files.item(0);
        if (file && !busy) onFile(file);
      }}
    >
      <UploadCloud className="dz-icon" aria-hidden="true" />
      <div className="dz-title">Drop the {scopeName} COSMA sales-rate report</div>
      <div className="hint">
        One row per element and Resource BU, twelve monthly rates and the annual Total. Rates are stored in{' '}
        {currency}.
      </div>
      <div className="btn-row dz-actions">
        <button type="button" className="btn btn-primary" disabled={busy} onClick={picker.open}>
          <UploadCloud aria-hidden="true" /> Choose file
        </button>
      </div>
      <div className="hint">The format and the header row are detected from the file&rsquo;s content</div>
      {picker.element}
    </div>
  );
}

export function SalesRatesFileCard({
  review,
  onReplace,
  busy,
}: {
  review: SalesRateReview;
  onReplace: (file: File) => void;
  busy: boolean;
}) {
  const { batch } = review;
  const picker = useFilePicker(onReplace, 'Replacement COSMA sales-rate file');
  const state = statePresentation(batch.state.code);
  const staged = batch.state.code === 'STAGED' || batch.state.code === 'AWAITING_RESOLUTION';
  return (
    <div className="card">
      <div className="card-head">
        <h3>File dropped</h3>
        {batch.state.code === 'COMMITTED' ? (
          <span className="badge pill-success">{batch.is_current ? 'Committed — current rates' : 'Committed'}</span>
        ) : staged ? (
          <span className="badge pill-info">Staged — not committed</span>
        ) : (
          <span className={`badge ${state.pill}`}>{state.label}</span>
        )}
      </div>
      <div className="card-body">
        <div className="dropzone is-filled">
          <div className="row us-eng-file">
            <FileSpreadsheet className="dz-icon" aria-hidden="true" />
            <div className="us-eng-file-text">
              <div className="dz-title truncate">{batch.original_filename ?? 'The uploaded file'}</div>
              <div className="hint">
                {formatBytes(batch.byte_size)} &middot; dropped {formatWhen(batch.uploaded_at)}
                {batch.uploaded_by ? ` by ${batch.uploaded_by.name}` : ''} &middot; batch #{batch.batch_number}
              </div>
            </div>
            <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={picker.open}>
              <Repeat aria-hidden="true" /> Replace
            </button>
          </div>
          {picker.element}
        </div>
      </div>
    </div>
  );
}

export function SalesRatesProcessingCard({ review }: { review: SalesRateReview }) {
  const { batch, issues } = review;
  return (
    <div className="card">
      <div className="card-head">
        <h3>What the system did with it</h3>
        <span className="hint">
          {issues.counts.ERROR ?? 0} error &middot; {issues.counts.WARNING ?? 0} warning &middot;{' '}
          {issues.counts.INFO ?? 0} info
        </span>
      </div>
      <div className="card-body stack">
        <dl className="dl cols-4">
          <div>
            <dt>Detected format</dt>
            <dd>
              {batch.detected_format} <span className="hint">sniffed by content</span>
            </dd>
          </div>
          <div>
            <dt>Header row</dt>
            <dd>{batch.detected_header_row === null ? <EmptyCell /> : batch.detected_header_row}</dd>
          </div>
          <div>
            <dt>Year bound / currency</dt>
            <dd>
              {review.period_year} <span className="hint">&middot; {batch.scope?.currency_code ?? '—'} from the scope</span>
            </dd>
          </div>
          <div>
            <dt>Rows parsed / kept / dropped</dt>
            <dd>
              {batch.rows_in ?? '—'} / {batch.rows_out ?? '—'} / {batch.rows_dropped ?? '—'}
            </dd>
          </div>
          <div>
            <dt>Checksum</dt>
            <dd className="mono">{shortChecksum(batch.content_sha256)}</dd>
          </div>
        </dl>

        <div>
          <div className="stat-label">Transformations applied</div>
          <ul className="stack-sm us-eng-list">
            {transformations(review).map((item) => (
              <li key={item.text} className="row us-eng-transform">
                {item.ok ? (
                  <CheckCircle2 className="us-eng-ok" aria-hidden="true" />
                ) : (
                  <AlertCircle className="us-eng-warn" aria-hidden="true" />
                )}
                <span>{item.text}</span>
              </li>
            ))}
          </ul>
        </div>

        {issues.items.length > 0 && (
          <div>
            <div className="stat-label">Issues</div>
            <ul className="stack-sm us-eng-list">
              {issues.items.map((item, index) => (
                <li key={`${item.code}-${index}`} className="row us-eng-issue">
                  <span className={severityPill(item.severity)}>{item.severity}</span>
                  <span className="mono">{item.code}</span>
                  <span className="us-eng-issue-text">
                    {item.message}
                    {item.column_name && <span className="hint"> &middot; {item.column_name}</span>}
                    {item.source_row_number !== null && (
                      <span className="hint"> &middot; row {item.source_row_number}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

/** One rate: the decimal, `—` for BLANK, a warning badge for ERROR. A null is never shown as 0. */
export function RateCellView({ cell, label }: { cell: RateCell; label: string }) {
  const kind = cellKind(cell);
  if (kind === 'error') {
    return (
      <span
        className="badge pill-warning kd-flag"
        title={`${label}: the source cell held an error — kept as a missing rate, never zero`}
        aria-label={`${label}: error in the source`}
      >
        <AlertTriangle aria-hidden="true" />
      </span>
    );
  }
  if (kind === 'blank') return <span className="hint" aria-label={`${label}: blank`}>—</span>;
  return <>{compactDecimal(cell.rate ?? '')}</>;
}

function Stat({ label, value, foot }: { label: string; value: number; foot: ReactNode }) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value.toLocaleString()}</div>
      <div className="stat-foot">{foot}</div>
    </div>
  );
}

export function SalesRatesRatesCard({
  figures,
  currency,
  footer,
}: {
  figures: SalesRateFigures;
  currency: string;
  footer?: ReactNode;
}) {
  return (
    <div className="card">
      <div className="card-head">
        <h3>Resulting rates</h3>
        <span className="hint">{currency} per manday · updated after every decision</span>
      </div>
      <div className="card-body stack">
        <div className="stat-grid">
          <Stat label="Rate keys" value={figures.keys} foot={`${figures.elements} elements · ${figures.duplicate_keys} duplicates`} />
          <Stat
            label="Matched to a line"
            value={figures.keys_matched}
            foot={`${figures.keys_kept} kept · ${figures.keys_excluded} excluded · ${figures.keys_undecided} undecided`}
          />
          <Stat
            label="Missing rates"
            value={figures.cells.ERROR ?? 0}
            foot={`${figures.annual_missing} annual · ${figures.cells.BLANK ?? 0} blank cells`}
          />
          <Stat
            label="Rate rows to write"
            value={figures.rate_rows_to_write}
            foot={`${figures.keys_new} new · ${figures.keys_refreshed} refreshed · ${figures.keys_dropped} dropped keys`}
          />
        </div>

        {figures.preview.length === 0 ? (
          <p className="hint">No rate results from this report yet.</p>
        ) : (
          <div className="table-scroll">
            <table className="data compact kd-lines">
              <thead>
                <tr>
                  <th>Element key</th>
                  <th>Decision</th>
                  {MONTH_LABELS.map((month) => (
                    <th key={month} className="num">
                      {month}
                    </th>
                  ))}
                  <th className="num">Annual</th>
                </tr>
              </thead>
              <tbody>
                {figures.preview.map((row) => (
                  <tr key={row.element_key}>
                    <td className="mono">{row.element_key}</td>
                    <td>
                      <span className={decisionPill(row.decision)}>{row.decision}</span>
                    </td>
                    {row.months.map((cell, index) => (
                      <td key={MONTH_LABELS[index]} className="num">
                        <RateCellView cell={cell} label={`${row.element_key} ${MONTH_LABELS[index]}`} />
                      </td>
                    ))}
                    <td className="num">
                      <RateCellView cell={row.annual} label={`${row.element_key} annual`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {figures.keys > figures.preview.length && (
          <p className="hint">
            Showing the first {figures.preview.length} of {figures.keys.toLocaleString()} keys, in key order.
          </p>
        )}
      </div>
      {footer}
    </div>
  );
}
